import React, {useCallback, useEffect, useRef, useState} from 'react';
import createAxiosInstance from '../../support/AxiosSupport';
import Spinner from '../Spinner';
import {formatDate} from '../../utils/dateTime';
import {AlertTriangleIcon, ArchiveIcon, DownloadIcon, RefreshIcon} from '../icons';

/**
 * Архив полного экспорта с фотографиями (CTHS-228).
 *
 * Сборка асинхронная: `POST /api/export/full/jobs` ставит задачу, планировщик собирает ZIP в
 * объектное хранилище, `GET .../jobs/{id}/download` отдаёт его потоком. Синхронного пути тут
 * быть не может — гардероб с фотографиями весит сотни мегабайт и упрётся в таймаут прокси.
 *
 * Блок показывает состояние последней задачи и, пока она в работе, перечитывает статус раз в
 * 5 секунд. Таймер снимается при размонтировании и при достижении терминального статуса —
 * иначе поллинг продолжает стучаться после ухода со страницы.
 *
 * Скачивание идёт авторизованным запросом в Blob, а не ссылкой `<a href>`: эндпоинт закрыт
 * JWT, голая ссылка вернула бы 401.
 */

type JobStatus = 'PENDING' | 'RUNNING' | 'READY' | 'FAILED' | 'EXPIRED';

interface ExportJob {
    id: string;
    status: JobStatus;
    sizeBytes: number | null;
    itemCount: number | null;
    photoCount: number | null;
    skippedPhotoCount: number | null;
    truncated: boolean;
    errorCode: string | null;
    createdAt: string | null;
    completedAt: string | null;
    expiresAt: string | null;
}

type BlockState = 'loading' | 'ready' | 'error';

/** Как часто перечитываем статус, пока архив собирается. */
const POLL_MS = 5000;

const DATE_OPTS: Intl.DateTimeFormatOptions = {day: 'numeric', month: 'long', year: 'numeric'};

const httpStatus = (e: unknown): number | undefined =>
    (e as {response?: {status?: number}})?.response?.status;

/** Текст отказа по коду: пользователю нужна причина, а не «что-то пошло не так». */
const createErrorText = (status?: number): string => {
    if (status === 409) {
        return 'Архив уже собирается — дождитесь готовности.';
    }
    if (status === 429) {
        return 'Собрать можно не больше трёх архивов в сутки. Попробуйте завтра.';
    }
    return 'Не удалось запросить архив. Попробуйте ещё раз.';
};

/** Размер файла человеку: байты в карточке никто не читает. */
const formatSize = (bytes: number | null): string => {
    if (!bytes || bytes <= 0) {
        return '';
    }
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} МБ` : `${Math.max(1, Math.round(bytes / 1024))} КБ`;
};

const isInProgress = (job: ExportJob | null): boolean =>
    job !== null && (job.status === 'PENDING' || job.status === 'RUNNING');

const FullExportZipBlock: React.FC = () => {
    const [state, setState] = useState<BlockState>('loading');
    const [job, setJob] = useState<ExportJob | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) {
            setState('loading');
            setError(null);
        }
        try {
            const api = await createAxiosInstance();
            const res = await api.get<ExportJob[]>('/export/full/jobs');
            setJob(res.data.length > 0 ? res.data[0] : null);
            setState('ready');
        } catch {
            if (!silent) {
                setState('error');
            }
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Поллинг живёт ровно столько, сколько задача в работе: терминальный статус и уход со
    // страницы одинаково его снимают.
    useEffect(() => {
        if (!isInProgress(job)) {
            return undefined;
        }
        timer.current = setTimeout(() => load(true), POLL_MS);
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
        };
    }, [job, load]);

    const create = async () => {
        setBusy(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<ExportJob>('/export/full/jobs');
            setJob(res.data);
            setState('ready');
        } catch (e: unknown) {
            setError(createErrorText(httpStatus(e)));
            if (httpStatus(e) === 409) {
                await load(true);
            }
        } finally {
            setBusy(false);
        }
    };

    const download = async () => {
        if (!job) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.get(`/export/full/jobs/${job.id}/download`, {responseType: 'blob'});
            const url = URL.createObjectURL(res.data as Blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'clothes-data-export.zip';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e: unknown) {
            const status = httpStatus(e);
            setError(status === 410
                ? 'Срок хранения архива истёк — соберите новый.'
                : 'Не удалось скачать архив. Попробуйте ещё раз.');
            await load(true);
        } finally {
            setBusy(false);
        }
    };

    if (state === 'loading') {
        return (
            <div className="py-4" data-testid="export-zip-loading">
                <Spinner/>
            </div>
        );
    }

    if (state === 'error') {
        return (
            <div className="space-y-3" data-testid="export-zip-error">
                <p className="text-sm text-red-600 dark:text-red-400">
                    Не удалось загрузить состояние архива.
                </p>
                <button type="button" onClick={() => load()} className="btn-secondary min-h-[44px]">
                    <RefreshIcon className="inline-block mr-2" size={18}/>
                    Повторить
                </button>
            </div>
        );
    }

    const hint = (
        <p className="text-xs text-gray-500 dark:text-gray-400">
            Архив содержит те же данные, что и JSON, плюс оригиналы фотографий вещей. Сборка
            занимает от нескольких секунд до нескольких минут; готовый файл хранится 7 дней.
        </p>
    );

    const errorLine = error && (
        <p className="text-sm text-red-600 dark:text-red-400" data-testid="export-zip-error-msg">
            {error}
        </p>
    );

    if (isInProgress(job)) {
        return (
            <div className="space-y-3" data-testid="export-zip-block">
                <p className="flex items-center gap-2 text-gray-900 dark:text-gray-100"
                   data-testid="export-zip-status">
                    <Spinner/>
                    Собираем архив — можно закрыть страницу и вернуться позже.
                </p>
                {errorLine}
                {hint}
            </div>
        );
    }

    if (job && job.status === 'READY') {
        return (
            <div className="space-y-3" data-testid="export-zip-block">
                <p className="text-gray-900 dark:text-gray-100" data-testid="export-zip-status">
                    Архив готов{formatSize(job.sizeBytes) ? ` · ${formatSize(job.sizeBytes)}` : ''}
                    {job.photoCount !== null ? ` · ${job.photoCount} фото` : ''}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    Доступен до {formatDate(job.expiresAt, DATE_OPTS)}. После этой даты файл
                    удаляется — соберите новый.
                </p>
                {job.truncated && (
                    <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                        <AlertTriangleIcon size={18} className="shrink-0 mt-0.5"/>
                        Архив усечён: часть фотографий не поместилась. Полные данные без фото —
                        в JSON-выгрузке.
                    </p>
                )}
                {errorLine}
                <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={download} disabled={busy}
                            className="btn-primary min-h-[44px] disabled:opacity-50"
                            data-testid="export-zip-download">
                        <DownloadIcon className="inline-block mr-2" size={18}/>
                        {busy ? 'Скачиваем…' : 'Скачать архив'}
                    </button>
                    <button type="button" onClick={create} disabled={busy}
                            className="btn-secondary min-h-[44px] disabled:opacity-50"
                            data-testid="export-zip-create">
                        <RefreshIcon className="inline-block mr-2" size={18}/>
                        Собрать заново
                    </button>
                </div>
                {hint}
            </div>
        );
    }

    const failed = job !== null && job.status === 'FAILED';
    const expired = job !== null && job.status === 'EXPIRED';
    const idleLabel = failed || expired ? 'Собрать заново' : 'Собрать архив';

    return (
        <div className="space-y-3" data-testid="export-zip-block">
            {failed && (
                <p className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400"
                   data-testid="export-zip-status">
                    <AlertTriangleIcon size={18} className="shrink-0 mt-0.5"/>
                    Прошлая сборка не удалась. Попробуйте ещё раз — данные не пострадали.
                </p>
            )}
            {expired && (
                <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="export-zip-status">
                    Срок хранения прошлого архива истёк, файл удалён.
                </p>
            )}
            {errorLine}
            <button type="button" onClick={create} disabled={busy}
                    className="btn-primary min-h-[44px] disabled:opacity-50"
                    data-testid="export-zip-create">
                <ArchiveIcon className="inline-block mr-2" size={18}/>
                {busy ? 'Запрашиваем…' : idleLabel}
            </button>
            {hint}
        </div>
    );
};

export default FullExportZipBlock;
